import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createSharedFinance,
  deleteSharedFinance,
  fetchDealDocumentsForDeal,
  fetchEntities,
  fetchSharedFinanceEvents,
  fetchSharedFinanceSessions,
  fetchSharedFinances,
  previewSharedFinance,
  reallocateSharedFinance,
  splitSharedFinanceProvisionally,
} from '../data/api';
import type { DealDocumentSummary, EntitySummary, SharedFinanceBasis, SharedFinanceEventOption, SharedFinancePreview, SharedFinanceRecord, SharedFinanceSessionOption } from '../data/api';
import { useConfirm } from './ConfirmDialog';
import FileDropzone from './FileDropzone';
import { MEDIA_ORIGIN } from '../auth/config';

// Capture a cost the feedlot incurred for everyone on site -- a month's feed,
// a shearing bill -- once, and let the server split it across deals by
// animal-days. The preview is the point of the screen: the operator sees the
// split before the money lands on anyone's ledger.

const zar = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 });
const fmtMoney = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : zar.format(value);
const fmtUnits = (value: number): string => value.toLocaleString('en-ZA', { maximumFractionDigits: 0 });
const fmtPct = (share: number): string => `${(share * 100).toFixed(1)}%`;
/**
 * A provisional split is always by animal-days, whatever basis the record
 * stores, so the column headings and unit words have to follow what was
 * actually computed rather than what the record says it is.
 */
const effectiveBasis = (record: SharedFinanceRecord): SharedFinanceBasis =>
  (record.isProvisional ? 'ANIMAL_DAYS' : record.basis);

const unitWordFor = (basis: SharedFinanceBasis): string =>
  basis === 'HEADCOUNT' ? 'head' : basis === 'RECEIVED_BATCH' ? 'head received' : basis === 'SESSION_ANIMALS' ? 'head on the sessions' : basis === 'EVENT_ANIMALS' ? 'head on the events' : 'animal-days';
const basisPhrase = (basis: SharedFinanceBasis): string =>
  basis === 'HEADCOUNT' ? 'by headcount' : basis === 'RECEIVED_BATCH' ? 'by received batch' : basis === 'SESSION_ANIMALS' ? 'by the animals on the sessions' : basis === 'EVENT_ANIMALS' ? 'equally by the animals on the events' : 'by animal-days';

// Movement events are what an invoice usually covers — a transporter bills a
// load, and the load is the receive event. The picker starts there and can
// widen to every event type.
const MOVEMENT_EVENT_CODES = ['FARM_REC', 'FEED_REC', 'ABAT_REC', 'FARM_DIS', 'FEED_DIS', 'ABAT_DIS'];
const EVENT_CODE_LABELS: Record<string, string> = {
  FARM_REC: 'Farm receive', FEED_REC: 'Feedlot receive', ABAT_REC: 'Abattoir receive',
  FARM_DIS: 'Farm dispatch', FEED_DIS: 'Feedlot dispatch', ABAT_DIS: 'Abattoir dispatch',
  FARM_REG: 'Farm registration', FEED_REG: 'Feedlot registration', ABAT_REG: 'Abattoir registration',
  FARM_SHE: 'Farm shearing', FEED_SHE: 'Feedlot shearing', FARM_WEI: 'Farm weigh', FEED_WEI: 'Feedlot weigh',
  ABAT_SLA: 'Slaughter', FEED_VAC: 'Feedlot vaccination', VACCINATION: 'Vaccination',
  FARM_MOR: 'Farm mortality', FEED_MOR: 'Feedlot mortality', ABAT_MOR: 'Abattoir mortality',
};
const eventCodeLabel = (code: string): string => EVENT_CODE_LABELS[code] || code;
const eventOptionLabel = (ev: SharedFinanceEventOption): string =>
  `${eventCodeLabel(ev.code)} · ${ev.eventDate || '—'}${ev.dealNo ? ` · ${ev.dealNo}` : ''}`;

const RECORD_TYPES: { value: string; label: string; hint: string }[] = [
  { value: 'FEED', label: 'Feed', hint: 'Feed delivered to the feedlot. Add the kilograms so each deal’s feed conversion is measured. An intake invoice for a batch (Kyto) splits by received batch; a top-up for everyone on site splits by animal-days.' },
  { value: 'SHEARING', label: 'Shearing', hint: 'The shearing bill for the sheep actually shorn — pick the shearing session(s) and split by the animals on them.' },
  { value: 'OTHER_COST', label: 'Other cost', hint: 'Wool broker charges, levies, anything else shared.' },
  { value: 'VETERINARY', label: 'Veterinary & health', hint: 'A per-head medicine or processing charge — on an intake invoice, split by received batch.' },
  { value: 'TRANSPORT', label: 'Transport', hint: 'A transport invoice — pick the receive (or dispatch) events the load covered, across deals if need be, and the cost is split equally by animal. If a journey record also carries this invoice’s amount, leave that journey’s cost blank so it is not counted twice.' },
  { value: 'WOOL_SALE', label: 'Wool sale (income)', hint: 'A clip sold for the whole feedlot: pick the shearing session(s) it came from and split by the sheep shorn.' },
  { value: 'OTHER_REVENUE', label: 'Other income', hint: '' },
];

const monthBounds = (value: string): { start: string; end: string } | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const last = new Date(year, month, 0).getDate();
  return { start: `${match[1]}-${match[2]}-01`, end: `${match[1]}-${match[2]}-${String(last).padStart(2, '0')}` };
};

interface FormState {
  entityId: string;
  recordType: string;
  amount: string;
  quantityKg: string;
  invoiceDate: string;
  periodStart: string;
  periodEnd: string;
  basis: SharedFinanceBasis;
  basisSessionIds: string[];
  basisEventIds: string[];
  /** Link the invoice already filed on the deal instead of uploading it again. */
  sourceDocumentId: string;
  description: string;
  referenceNumber: string;
  document: File | null;
}

const EMPTY_FORM: FormState = {
  entityId: '',
  recordType: 'FEED',
  amount: '',
  quantityKg: '',
  invoiceDate: '',
  periodStart: '',
  periodEnd: '',
  basis: 'ANIMAL_DAYS',
  basisSessionIds: [],
  basisEventIds: [],
  sourceDocumentId: '',
  description: '',
  referenceNumber: '',
  document: null,
};

const mediaUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${MEDIA_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

export interface SharedFeedlotCostsProps {
  /**
   * Embedded on a deal's Documents tab: the form opens on the event basis with
   * this deal's events listed first, and the captured list shows only records
   * that reached this deal.
   */
  dealId?: string;
  dealLabel?: string;
  /** Bumped by the host when something changed elsewhere (a modal closed). */
  refreshToken?: number;
}

export default function SharedFeedlotCosts({ dealId, dealLabel, refreshToken = 0 }: SharedFeedlotCostsProps = {}) {
  const confirm = useConfirm();
  const embedded = Boolean(dealId);
  const initialForm = useMemo<FormState>(
    () => (embedded ? { ...EMPTY_FORM, basis: 'EVENT_ANIMALS', recordType: 'TRANSPORT' } : EMPTY_FORM),
    [embedded],
  );
  const [entities, setEntities] = useState<EntitySummary[]>([]);
  const [records, setRecords] = useState<SharedFinanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>(initialForm);
  const [month, setMonth] = useState('');
  const [preview, setPreview] = useState<SharedFinancePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [filterEntity, setFilterEntity] = useState('');
  const [sessionOptions, setSessionOptions] = useState<SharedFinanceSessionOption[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  // Event picker (EVENT_ANIMALS): which events the invoice covered.
  const [eventOptions, setEventOptions] = useState<SharedFinanceEventOption[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventScope, setEventScope] = useState<'deal' | 'all'>(embedded ? 'deal' : 'all');
  const [movementOnly, setMovementOnly] = useState(true);
  const [eventSearch, setEventSearch] = useState('');
  // Ticked events stay visible even when the filter no longer lists them.
  const [pickedEvents, setPickedEvents] = useState<Record<string, SharedFinanceEventOption>>({});
  // The deal's filed documents, offered as the record's evidence when embedded.
  const [dealDocuments, setDealDocuments] = useState<DealDocumentSummary[]>([]);
  useEffect(() => {
    if (!dealId) { setDealDocuments([]); return; }
    fetchDealDocumentsForDeal(dealId).then(setDealDocuments).catch(() => setDealDocuments([]));
  }, [dealId, refreshToken]);

  const feedlots = useMemo(
    () => entities.filter((e) => (e.entityType || '').toUpperCase() === 'FEEDLOT').sort((a, b) => a.entityName.localeCompare(b.entityName)),
    [entities],
  );
  const otherEntities = useMemo(
    () => entities.filter((e) => (e.entityType || '').toUpperCase() !== 'FEEDLOT').sort((a, b) => a.entityName.localeCompare(b.entityName)),
    [entities],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entityRows, sharedRows] = await Promise.all([
        fetchEntities(),
        fetchSharedFinances(dealId ? { dealId } : filterEntity ? { entityId: filterEntity } : {}),
      ]);
      setEntities(entityRows);
      setRecords(sharedRows);
    } catch (err: any) {
      setError(err?.message || 'Could not load shared costs.');
    } finally {
      setLoading(false);
    }
  }, [filterEntity, dealId]);

  useEffect(() => { load(); }, [load, refreshToken]);

  // Any change to the inputs that drive the split invalidates the preview.
  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (['entityId', 'amount', 'quantityKg', 'periodStart', 'periodEnd', 'basis', 'basisSessionIds', 'basisEventIds'].includes(key as string)) setPreview(null);
    setSuccess('');
  };

  // Event picker: the events an invoice covered. On a deal's own tab it lists
  // that deal's events first; "every deal" widens it so one transport bill can
  // reach the loads of several deals.
  useEffect(() => {
    if (form.basis !== 'EVENT_ANIMALS') { setEventOptions([]); return; }
    let cancelled = false;
    setLoadingEvents(true);
    fetchSharedFinanceEvents({
      dealId: eventScope === 'deal' ? dealId : undefined,
      entityId: form.entityId || undefined,
      codes: movementOnly ? MOVEMENT_EVENT_CODES : undefined,
      start: form.periodStart || undefined,
      end: form.periodEnd || undefined,
    })
      .then((rows) => { if (!cancelled) setEventOptions(rows); })
      .catch(() => { if (!cancelled) setEventOptions([]); })
      .finally(() => { if (!cancelled) setLoadingEvents(false); });
    return () => { cancelled = true; };
  }, [form.basis, form.entityId, form.periodStart, form.periodEnd, eventScope, movementOnly, dealId, refreshToken]);

  const toggleEvent = (ev: SharedFinanceEventOption) => {
    setForm((prev) => ({ ...prev, basisEventIds: prev.basisEventIds.includes(ev.id) ? prev.basisEventIds.filter((x) => x !== ev.id) : [...prev.basisEventIds, ev.id] }));
    setPickedEvents((prev) => ({ ...prev, [ev.id]: ev }));
    setPreview(null);
  };
  const visibleEvents = useMemo(() => {
    const needle = eventSearch.trim().toLowerCase();
    if (!needle) return eventOptions;
    return eventOptions.filter((ev) => [ev.dealNo, ev.dealName, ev.code, eventCodeLabel(ev.code), ev.entityName, ev.eventDate].some((v) => String(v || '').toLowerCase().includes(needle)));
  }, [eventOptions, eventSearch]);
  const pickedSummary = useMemo(() => {
    const picked = form.basisEventIds.map((id) => pickedEvents[id]).filter(Boolean);
    const deals = new Set(picked.map((ev) => ev.dealNo || ev.dealId || ''));
    return { count: picked.length, animals: picked.reduce((sum, ev) => sum + ev.animalCount, 0), deals: deals.size };
  }, [form.basisEventIds, pickedEvents]);

  // Session picker: the recorded sessions at the feedlot in the period, so a
  // shearing bill or a wool clip can name the sheep actually shorn.
  useEffect(() => {
    if (form.basis !== 'SESSION_ANIMALS' || !form.entityId) { setSessionOptions([]); return; }
    let cancelled = false;
    setLoadingSessions(true);
    fetchSharedFinanceSessions({ entityId: form.entityId, start: form.periodStart || undefined, end: form.periodEnd || undefined })
      .then((rows) => { if (!cancelled) setSessionOptions(rows); })
      .catch(() => { if (!cancelled) setSessionOptions([]); })
      .finally(() => { if (!cancelled) setLoadingSessions(false); });
    return () => { cancelled = true; };
  }, [form.basis, form.entityId, form.periodStart, form.periodEnd]);

  const toggleSession = (id: string) => {
    setForm((prev) => ({ ...prev, basisSessionIds: prev.basisSessionIds.includes(id) ? prev.basisSessionIds.filter((x) => x !== id) : [...prev.basisSessionIds, id] }));
    setPreview(null);
  };

  const applyMonth = (value: string) => {
    setMonth(value);
    const bounds = monthBounds(value);
    if (bounds) {
      setForm((prev) => ({ ...prev, periodStart: bounds.start, periodEnd: bounds.end, invoiceDate: prev.invoiceDate || bounds.end }));
      setPreview(null);
    }
  };

  const isEventBasis = form.basis === 'EVENT_ANIMALS';
  const canPreview = isEventBasis
    ? Boolean(form.amount && Number(form.amount) > 0 && form.basisEventIds.length > 0)
    : Boolean(form.entityId && form.amount && Number(form.amount) > 0 && form.periodStart && (form.basis === 'HEADCOUNT' || form.periodEnd) && (form.basis !== 'SESSION_ANIMALS' || form.basisSessionIds.length > 0));

  const runPreview = async () => {
    if (!canPreview) return;
    setPreviewing(true);
    setError('');
    try {
      const result = await previewSharedFinance({
        entityId: isEventBasis ? undefined : form.entityId,
        basis: form.basis,
        periodStart: isEventBasis ? undefined : form.periodStart,
        periodEnd: isEventBasis ? undefined : form.basis === 'HEADCOUNT' ? form.periodStart : form.periodEnd,
        amount: form.amount,
        quantityKg: form.quantityKg || undefined,
        basisSessionIds: form.basis === 'SESSION_ANIMALS' ? form.basisSessionIds : undefined,
        basisEventIds: isEventBasis ? form.basisEventIds : undefined,
      });
      setPreview(result);
    } catch (err: any) {
      setError(err?.message || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!preview) {
      setError('Preview the split before saving, so you can see which deals will be charged.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await createSharedFinance({
        entityId: isEventBasis ? undefined : form.entityId,
        recordType: form.recordType,
        amount: form.amount,
        quantityKg: form.quantityKg || undefined,
        invoiceDate: form.invoiceDate || form.periodEnd || form.periodStart || new Date().toISOString().slice(0, 10),
        periodStart: isEventBasis ? undefined : form.periodStart,
        periodEnd: isEventBasis ? undefined : form.basis === 'HEADCOUNT' ? form.periodStart : form.periodEnd,
        basis: form.basis,
        basisSessionIds: form.basis === 'SESSION_ANIMALS' ? form.basisSessionIds : undefined,
        basisEventIds: isEventBasis ? form.basisEventIds : undefined,
        sourceDocumentId: form.sourceDocumentId || undefined,
        description: form.description,
        referenceNumber: form.referenceNumber || undefined,
        document: form.sourceDocumentId ? null : form.document,
      });
      setSuccess(
        created.warning
          ? created.warning
          : `Saved. ${fmtMoney(created.amount)} split across ${created.allocations.length} deal${created.allocations.length === 1 ? '' : 's'} ${basisPhrase(created.basis)}.`,
      );
      setForm((prev) => ({ ...initialForm, entityId: prev.entityId, recordType: prev.recordType, basis: prev.basis }));
      setMonth('');
      setPreview(null);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (record: SharedFinanceRecord) => {
    const ok = await confirm({
      title: `Delete "${record.description}"?`,
      message: `${fmtMoney(record.amount)}. Its share is removed from every deal's ledger.`,
      confirmLabel: 'Delete cost',
      tone: 'danger',
    });
    if (!ok) return;
    setError('');
    try {
      await deleteSharedFinance(record.id);
      setSuccess('Deleted. The deals no longer carry that charge.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Delete failed.');
    }
  };

  const reallocate = async (record: SharedFinanceRecord) => {
    setError('');
    try {
      const wasProvisional = record.isProvisional;
      const updated = await reallocateSharedFinance(record.id);
      setRecords((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setSuccess(
        wasProvisional
          ? `Locked the final split of "${updated.description}" over ${fmtUnits(updated.allocationTotalUnits || 0)} ${unitWordFor(updated.basis)}.`
          : `Re-split "${updated.description}" over ${fmtUnits(updated.allocationTotalUnits || 0)} ${unitWordFor(updated.basis)}.`,
      );
    } catch (err: any) {
      setError(err?.message || 'Reallocate failed.');
    }
  };

  const splitProvisionally = async (record: SharedFinanceRecord) => {
    setError('');
    try {
      const updated = await splitSharedFinanceProvisionally(record.id);
      setRecords((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setSuccess(
        `Provisional split of "${updated.description}" over ${fmtUnits(updated.allocationTotalUnits || 0)} animal-days to date. `
        + `${updated.openDealCount} deal${updated.openDealCount === 1 ? '' : 's'} still open, so the shares will move; each close recomputes them.`,
      );
    } catch (err: any) {
      setError(err?.message || 'Could not compute the provisional split.');
    }
  };

  const selectedType = RECORD_TYPES.find((t) => t.value === form.recordType);
  const unitWord = unitWordFor(form.basis);

  return (
    <section className={embedded ? 'min-w-0' : 'rounded-2xl border border-[#1e6bb824] bg-white p-4 shadow-sm md:p-5'}>
      {!embedded && (
        <div className="admin-section-head mb-4 flex items-center justify-between gap-2">
          <div>
            <h2 className={embedded ? 'text-lg font-semibold text-avcs-text' : 'text-2xl font-semibold text-avcs-text'}>{embedded ? 'Costs on events' : 'Shared feedlot costs'}</h2>
            <p className="mt-0.5 text-sm text-avcs-muted">
              {embedded
                ? `An invoice for one or more of ${dealLabel ? `deal ${dealLabel}'s` : "this deal's"} events — a transport bill for a load, say — captured once and split equally across the animals on the events you pick. Pick events from other deals too when one invoice covered several loads.`
                : 'A cost the feedlot incurred for everyone on site, captured once and split across deals by how many days each animal was there.'}
            </p>
          </div>
          <button className="admin-inline-action btn" type="button" onClick={() => { setForm(initialForm); setMonth(''); setPreview(null); setError(''); setSuccess(''); }}>Clear</button>
        </div>
      )}

      {error && <div className="mb-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>}
      {success && <div className="mb-3 rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]">{success}</div>}

      <form className="mb-5 rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-4" onSubmit={submit}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-avcs-text">{embedded ? 'New cost on events' : 'New shared cost'}</h3>
          {embedded && (
            <button className="admin-inline-action btn" type="button" onClick={() => { setForm(initialForm); setMonth(''); setPreview(null); setError(''); setSuccess(''); }}>Clear</button>
          )}
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <label className="grid gap-1 text-sm text-avcs-text">
            {isEventBasis ? <>Entity <span className="text-avcs-muted">(optional — narrows the event list)</span></> : 'Feedlot'}
            <select className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.entityId} onChange={(e) => setField('entityId', e.target.value)} required={!isEventBasis}>
              <option value="">{isEventBasis ? 'Any entity' : 'Select the feedlot…'}</option>
              {feedlots.map((e) => <option key={e.id} value={e.id}>{e.entityName}</option>)}
              {otherEntities.length > 0 && (
                <optgroup label="Other entities">
                  {otherEntities.map((e) => <option key={e.id} value={e.id}>{e.entityName}</option>)}
                </optgroup>
              )}
            </select>
          </label>
          <label className="grid gap-1 text-sm text-avcs-text">
            What is it
            <select className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.recordType} onChange={(e) => setField('recordType', e.target.value)}>
              {RECORD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {selectedType?.hint && <span className="text-xs text-avcs-muted">{selectedType.hint}</span>}
          </label>
          <label className="grid gap-1 text-sm text-avcs-text">
            Split by
            <select className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.basis} onChange={(e) => setField('basis', e.target.value as SharedFinanceBasis)}>
              <option value="ANIMAL_DAYS">Animal-days in the period</option>
              <option value="RECEIVED_BATCH">Animals received in the period</option>
              <option value="SESSION_ANIMALS">Animals on the selected sessions</option>
              <option value="EVENT_ANIMALS">Animals on the selected events</option>
              <option value="HEADCOUNT">Animals present on one day</option>
            </select>
            <span className="text-xs text-avcs-muted">
              {form.basis === 'ANIMAL_DAYS'
                ? 'An animal there for 13 of 31 days carries 13/31 of a full share.'
                : form.basis === 'RECEIVED_BATCH'
                  ? 'Every animal whose stay began in the period carries one equal share — an intake invoice.'
                  : form.basis === 'SESSION_ANIMALS'
                    ? 'Every distinct animal on the sessions you tick below carries one equal share — a shearing bill, a wool clip.'
                    : form.basis === 'EVENT_ANIMALS'
                      ? 'Every distinct animal on the events you tick below carries one equal share, whichever deal it belongs to — a transport invoice for the loads it covered.'
                      : 'Every animal on site that day carries one equal share.'}
            </span>
          </label>

          <label className="grid gap-1 text-sm text-avcs-text">
            Amount (ex VAT, Rand)
            <input type="number" min={0} step="0.01" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.amount} onChange={(e) => setField('amount', e.target.value)} required />
          </label>
          {form.recordType === 'FEED' && (
            <label className="grid gap-1 text-sm text-avcs-text">
              Kilograms delivered <span className="text-avcs-muted">(optional, makes FCR measured)</span>
              <input type="number" min={0} step="0.001" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.quantityKg} onChange={(e) => setField('quantityKg', e.target.value)} />
            </label>
          )}
          <label className="grid gap-1 text-sm text-avcs-text">
            Invoice date
            <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.invoiceDate} onChange={(e) => setField('invoiceDate', e.target.value)} />
          </label>

          {isEventBasis ? (
            <>
              <label className="grid gap-1 text-sm text-avcs-text">
                Events from <span className="text-avcs-muted">(optional filter)</span>
                <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.periodStart} onChange={(e) => setField('periodStart', e.target.value)} />
              </label>
              <label className="grid gap-1 text-sm text-avcs-text">
                Events to <span className="text-avcs-muted">(optional filter)</span>
                <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.periodEnd} onChange={(e) => setField('periodEnd', e.target.value)} />
              </label>
            </>
          ) : form.basis !== 'HEADCOUNT' ? (
            <>
              {form.basis === 'ANIMAL_DAYS' ? (
                <label className="grid gap-1 text-sm text-avcs-text">
                  Month <span className="text-avcs-muted">(fills the period)</span>
                  <input type="month" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={month} onChange={(e) => applyMonth(e.target.value)} />
                </label>
              ) : (
                <label className="grid gap-1 text-sm text-avcs-text">
                  Receive date <span className="text-avcs-muted">(fills both dates)</span>
                  <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.periodStart === form.periodEnd ? form.periodStart : ''} onChange={(e) => { setMonth(''); setForm((prev) => ({ ...prev, periodStart: e.target.value, periodEnd: e.target.value, invoiceDate: prev.invoiceDate })); setPreview(null); }} />
                </label>
              )}
              <label className="grid gap-1 text-sm text-avcs-text">
                {form.basis === 'RECEIVED_BATCH' ? 'Received from' : 'Period start'}
                <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.periodStart} onChange={(e) => { setMonth(''); setField('periodStart', e.target.value); }} required />
              </label>
              <label className="grid gap-1 text-sm text-avcs-text">
                {form.basis === 'RECEIVED_BATCH' ? 'Received to' : 'Period end'}
                <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.periodEnd} onChange={(e) => { setMonth(''); setField('periodEnd', e.target.value); }} required />
              </label>
            </>
          ) : (
            <label className="grid gap-1 text-sm text-avcs-text">
              Date the animals were counted
              <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.periodStart} onChange={(e) => setField('periodStart', e.target.value)} required />
            </label>
          )}

          {form.basis === 'SESSION_ANIMALS' && (
            <div className="grid gap-1 text-sm text-avcs-text md:col-span-2 xl:col-span-3">
              Sessions whose animals share this record
              {!form.entityId && <span className="text-xs text-avcs-muted">Pick the feedlot first.</span>}
              {form.entityId && loadingSessions && <span className="text-xs text-avcs-muted">Loading sessions…</span>}
              {form.entityId && !loadingSessions && sessionOptions.length === 0 && (
                <span className="text-xs text-avcs-muted">No recorded sessions at this feedlot in the period. Widen the dates.</span>
              )}
              {sessionOptions.length > 0 && (
                <div className="grid gap-1 rounded-lg border border-[#1e6bb833] bg-white p-2 sm:grid-cols-2">
                  {sessionOptions.map((opt) => {
                    const checked = form.basisSessionIds.includes(opt.id);
                    return (
                      <label key={opt.id} className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm ${checked ? 'bg-[#f1f7ff]' : 'hover:bg-[#fbfeff]'}`}>
                        <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggleSession(opt.id)} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-avcs-text">{opt.label}</span>
                          <span className="block text-xs text-avcs-muted">
                            {opt.eventDate || '—'} · {opt.eventTypes.join(', ')} · {opt.animalCount} animals · {Object.entries(opt.deals).map(([d, n]) => `${d} ${n}`).join(', ')}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isEventBasis && (
            <div className="grid gap-2 text-sm text-avcs-text md:col-span-2 xl:col-span-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>Events the invoice covered</span>
                <span className="text-xs text-avcs-muted">
                  {pickedSummary.count === 0
                    ? 'Nothing picked yet.'
                    : `${pickedSummary.count} event${pickedSummary.count === 1 ? '' : 's'} · ${pickedSummary.animals} animals · ${pickedSummary.deals} deal${pickedSummary.deals === 1 ? '' : 's'}`}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                {embedded && (
                  <div className="flex gap-1 rounded-lg border border-[#1e6bb833] p-0.5">
                    <button type="button" className={`rounded-md px-3 py-1 font-semibold ${eventScope === 'deal' ? 'bg-[#1e6bb8] text-white' : 'text-avcs-muted hover:text-avcs-text'}`} onClick={() => setEventScope('deal')}>This deal</button>
                    <button type="button" className={`rounded-md px-3 py-1 font-semibold ${eventScope === 'all' ? 'bg-[#1e6bb8] text-white' : 'text-avcs-muted hover:text-avcs-text'}`} onClick={() => setEventScope('all')}>Every deal</button>
                  </div>
                )}
                <label className="flex items-center gap-1.5 text-avcs-text">
                  <input type="checkbox" checked={movementOnly} onChange={(e) => setMovementOnly(e.target.checked)} />
                  Movement events only (receive / dispatch)
                </label>
                <input
                  className="min-w-[12rem] flex-1 rounded-lg border border-[#1e6bb833] px-2 py-1.5"
                  placeholder="Filter by deal, entity, date…"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                />
              </div>
              {loadingEvents && <span className="text-xs text-avcs-muted">Loading events…</span>}
              {!loadingEvents && visibleEvents.length === 0 && (
                <span className="text-xs text-avcs-muted">
                  No events match. {embedded && eventScope === 'deal' ? 'Switch to "Every deal", ' : ''}untick "Movement events only", or widen the dates.
                </span>
              )}
              {visibleEvents.length > 0 && (
                <div className="grid max-h-72 gap-1 overflow-y-auto rounded-lg border border-[#1e6bb833] bg-white p-2 sm:grid-cols-2">
                  {visibleEvents.map((ev) => {
                    const checked = form.basisEventIds.includes(ev.id);
                    return (
                      <label key={ev.id} className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm ${checked ? 'bg-[#f1f7ff]' : 'hover:bg-[#fbfeff]'}`}>
                        <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggleEvent(ev)} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-avcs-text">{eventOptionLabel(ev)}</span>
                          <span className="block text-xs text-avcs-muted">
                            {ev.dealName ? `${ev.dealName} · ` : ''}{ev.animalCount} animal{ev.animalCount === 1 ? '' : 's'}{ev.entityName ? ` · at ${ev.entityName}` : ''}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {form.basisEventIds.some((id) => !visibleEvents.some((ev) => ev.id === id)) && (
                <div className="flex flex-wrap gap-1 text-xs">
                  {form.basisEventIds.filter((id) => !visibleEvents.some((ev) => ev.id === id)).map((id) => {
                    const ev = pickedEvents[id];
                    return (
                      <button key={id} type="button" className="rounded-full border border-[#1e6bb833] bg-[#f1f7ff] px-2 py-0.5 text-[#0f3b64]" title="Untick" onClick={() => ev && toggleEvent(ev)}>
                        {ev ? eventOptionLabel(ev) : id} ×
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <label className="grid gap-1 text-sm text-avcs-text">
            Reference <span className="text-avcs-muted">(invoice no)</span>
            <input className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.referenceNumber} onChange={(e) => setField('referenceNumber', e.target.value)} />
          </label>
          <label className="grid gap-1 text-sm text-avcs-text md:col-span-2">
            Description
            <input className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder={isEventBasis ? 'e.g. Freek Pieterse Vervoer, 3 loads to Damspruit (INV0000539)' : 'e.g. Kyto feed, May 2026 (invoice 3000662)'} required />
          </label>
          <div className="grid gap-1 text-sm text-avcs-text md:col-span-2 xl:col-span-3">
            Invoice or statement <span className="text-avcs-muted">(optional)</span>
            {embedded && dealDocuments.length > 0 && (
              <select className="rounded-lg border border-[#1e6bb833] bg-white px-3 py-2" value={form.sourceDocumentId} onChange={(e) => setField('sourceDocumentId', e.target.value)}>
                <option value="">Upload a new file below…</option>
                <optgroup label="Link a document already filed on this deal">
                  {dealDocuments.map((doc) => (
                    <option key={doc.id} value={doc.id}>{[doc.documentTypeName, doc.referenceNumber ? `Ref ${doc.referenceNumber}` : '', doc.dateOfDocument].filter(Boolean).join(' · ')}</option>
                  ))}
                </optgroup>
              </select>
            )}
            {!form.sourceDocumentId && <FileDropzone file={form.document} onFileChange={(file: File | null) => setField('document', file)} acceptedExtensions={['pdf', 'jpg', 'jpeg', 'png', 'xlsx', 'csv']} />}
            {form.sourceDocumentId && <span className="text-xs text-avcs-muted">The record will open that document — no second copy is uploaded.</span>}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button className="btn" type="button" onClick={runPreview} disabled={!canPreview || previewing}>
            {previewing ? 'Working out the split…' : 'Preview split'}
          </button>
          <button className="btn primary" type="submit" disabled={!preview || saving || preview.rows.length === 0}>
            {saving ? 'Saving…' : 'Save and charge the deals'}
          </button>
          {!preview && <span className="text-xs text-avcs-muted">Preview first — saving charges each deal’s ledger.</span>}
        </div>

        {preview && (
          <div className="mt-4 rounded-xl border border-[#1e6bb824] bg-white p-3">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <div className="text-sm font-semibold text-avcs-text">
                {preview.rows.length === 0
                  ? isEventBasis ? 'The picked events carry no animals with a deal.' : `No animals with a deal were at this feedlot in the period.`
                  : `${fmtUnits(preview.totalUnits)} ${unitWord} across ${preview.rows.length} deal${preview.rows.length === 1 ? '' : 's'}`}
              </div>
              {preview.unallocatedUnits > 0 && (
                <div className="text-xs text-[#7b4a18]">
                  {fmtUnits(preview.unallocatedUnits)} {unitWord} on {preview.unallocatedAnimals} animal{preview.unallocatedAnimals === 1 ? '' : 's'} with no deal are not charged to anyone.
                </div>
              )}
            </div>
            {preview.rows.length > 0 && (
              <div className="admin-table-scroll overflow-x-auto">
                <table className="w-full min-w-[520px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-[#1e6bb824] text-left text-xs uppercase tracking-wide text-avcs-muted">
                      <th className="px-2 py-1.5 font-semibold">Deal</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Animals</th>
                      <th className="px-2 py-1.5 text-right font-semibold">{form.basis === 'ANIMAL_DAYS' ? 'Animal-days' : 'Head'}</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Share</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                      {form.quantityKg && <th className="px-2 py-1.5 text-right font-semibold">Kg</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.dealId} className="border-b border-[#1e6bb814]">
                        <td className="px-2 py-1.5 text-avcs-text">{row.dealNo || row.dealId}{row.dealName ? <span className="text-avcs-muted"> · {row.dealName}</span> : null}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{row.animalCount}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtUnits(row.units)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(row.share)}</td>
                        <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{fmtMoney(row.amount)}</td>
                        {form.quantityKg && <td className="px-2 py-1.5 text-right tabular-nums">{row.quantityKg === null ? '—' : row.quantityKg.toLocaleString('en-ZA', { maximumFractionDigits: 1 })}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </form>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-avcs-text">{embedded ? 'Shared costs reaching this deal' : 'Captured shared costs'}</h3>
        {!embedded && (
          <label className="flex items-center gap-2 text-sm text-avcs-text">
            Feedlot
            <select className="rounded-lg border border-[#1e6bb833] px-2 py-1.5 text-sm" value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)}>
              <option value="">All</option>
              {feedlots.map((e) => <option key={e.id} value={e.id}>{e.entityName}</option>)}
            </select>
          </label>
        )}
      </div>
      {loading && records.length === 0 && <p className="text-sm text-avcs-muted">Loading…</p>}
      {!loading && records.length === 0 && <p className="text-sm text-avcs-muted">Nothing captured yet.</p>}
      <div className="grid gap-2">
        {records.map((record) => {
          const isOpen = Boolean(expanded[record.id]);
          const href = mediaUrl(record.documentUrl);
          const shown = effectiveBasis(record);
          // Dashed edge = not settled. The same device marks the card, the
          // badge and the split's rows, so "this will move" reads the same
          // way wherever the eye lands.
          return (
            <div
              key={record.id}
              className={`rounded-xl border bg-white p-3 ${record.isProvisional ? 'border-dashed border-[#e0c48a]' : 'border-solid border-[#1e6bb824]'}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span className="font-semibold text-avcs-text">{record.description}</span>
                    <span className="rounded border border-[#1e6bb824] bg-[#fbfeff] px-1.5 py-0.5 text-[11px] font-semibold text-avcs-muted">{record.recordTypeLabel}</span>
                    {record.isProvisional && (
                      <span className="rounded border border-dashed border-[#d3ab63] bg-[#fdf6e7] px-1.5 py-0.5 text-[11px] font-semibold text-[#5c4300]">Provisional</span>
                    )}
                    {record.referenceNumber && <span className="text-xs text-avcs-muted">Ref {record.referenceNumber}</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-avcs-muted">
                    {record.entityName || 'Selected events'} · {record.periodStart === record.periodEnd ? record.periodStart : `${record.periodStart} to ${record.periodEnd}`} · {basisPhrase(shown)}
                    {record.allocationTotalUnits !== null ? ` · ${fmtUnits(record.allocationTotalUnits)} ${unitWordFor(shown)}` : ''}
                    {record.quantityKg !== null ? ` · ${record.quantityKg.toLocaleString('en-ZA', { maximumFractionDigits: 0 })} kg` : ''}
                    {href && <> · <a className="font-semibold text-[#1e6bb8] hover:underline" href={href} target="_blank" rel="noreferrer">Open document</a></>}
                    {!href && record.sourceDocumentUrl && <> · <a className="font-semibold text-[#1e6bb8] hover:underline" href={mediaUrl(record.sourceDocumentUrl) || '#'} target="_blank" rel="noreferrer" title={record.sourceDocumentName || 'Linked document'}>Open {record.sourceDocumentName || 'linked document'}</a></>}
                  </div>
                  {record.basisSessions.length > 0 && (
                    <div className="mt-0.5 text-xs text-avcs-muted">Sessions: {record.basisSessions.map((x) => x.label).join(' · ')}</div>
                  )}
                  {record.basisEvents.length > 0 && (
                    <div className="mt-0.5 text-xs text-avcs-muted">Events: {record.basisEvents.map((ev) => `${eventOptionLabel(ev)} (${ev.animalCount})`).join(' · ')}</div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold tabular-nums text-avcs-text">{fmtMoney(record.amount)}</span>
                  <button type="button" className="rounded-md border border-[#1e6bb824] bg-white px-2 py-1 text-xs font-semibold text-[#1e6bb8] hover:bg-[#f1f7ff]" onClick={() => setExpanded((prev) => ({ ...prev, [record.id]: !isOpen }))}>
                    {isOpen ? 'Hide split' : `${record.allocations.length} deal${record.allocations.length === 1 ? '' : 's'}`}
                  </button>
                  {record.canSplitProvisionally && !record.isProvisional && (
                    <button
                      type="button"
                      className="rounded-md border border-dashed border-[#d3ab63] bg-[#fdf6e7] px-2 py-1 text-xs font-semibold text-[#5c4300] hover:bg-[#fbf0d8]"
                      title="Split by animal-days as of today. The shares stay provisional and move as each deal closes."
                      onClick={() => splitProvisionally(record)}
                    >
                      Provisional split
                    </button>
                  )}
                  <button
                    type="button"
                    className="rounded-md border border-[#1e6bb824] bg-white px-2 py-1 text-xs font-semibold text-avcs-text hover:bg-[#f1f7ff]"
                    title={record.isProvisional
                      ? 'Recompute on the record\u2019s own basis and settle the shares.'
                      : 'Recompute the split from the current movement events'}
                    onClick={() => reallocate(record)}
                  >
                    {record.isProvisional ? 'Lock final split' : 'Re-split'}
                  </button>
                  <button type="button" className="rounded-md border border-[#e7bbb9] bg-white px-2 py-1 text-xs font-semibold text-[#6b1010] hover:bg-[#fdf1ef]" onClick={() => remove(record)}>Delete</button>
                </div>
              </div>
              {record.provisionalNote && (
                <p className="mt-2 rounded-lg border border-dashed border-[#e0c48a] bg-[#fdf6e7] px-2 py-1.5 text-xs text-[#5c4300]">{record.provisionalNote}</p>
              )}
              {record.allocations.length === 0 && (
                <p className="mt-2 text-xs text-[#7b4a18]">Not charged to any deal: {record.basis === 'EVENT_ANIMALS' ? 'the picked events carry no animals with a deal' : `no animals with a deal were at ${record.entityName} in this period`}. Load the movement events, then Re-split.</p>
              )}
              {isOpen && record.allocations.length > 0 && (
                <div className="admin-table-scroll mt-2 overflow-x-auto">
                  <table className="w-full min-w-[480px] border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-[#1e6bb824] text-left text-xs uppercase tracking-wide text-avcs-muted">
                        <th className="px-2 py-1.5 font-semibold">Deal</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Animals</th>
                        <th className="px-2 py-1.5 text-right font-semibold">{shown === 'ANIMAL_DAYS' ? (record.isProvisional ? 'Animal-days so far' : 'Animal-days') : 'Head'}</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Share</th>
                        <th className="px-2 py-1.5 text-right font-semibold">{record.isProvisional ? 'Amount so far' : 'Amount'}</th>
                        {record.quantityKg !== null && <th className="px-2 py-1.5 text-right font-semibold">Kg</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {record.allocations.map((a) => (
                        <tr key={a.id} className={record.isProvisional ? 'border-b border-dashed border-[#e0c48a]' : 'border-b border-[#1e6bb814]'}>
                          <td className="px-2 py-1.5 text-avcs-text">{a.dealNo}{a.dealName ? <span className="text-avcs-muted"> · {a.dealName}</span> : null}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{a.animalCount}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmtUnits(a.units)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(a.share)}</td>
                          <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{fmtMoney(a.amount)}</td>
                          {record.quantityKg !== null && <td className="px-2 py-1.5 text-right tabular-nums">{a.quantityKg === null ? '—' : a.quantityKg.toLocaleString('en-ZA', { maximumFractionDigits: 1 })}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
